/*
  O atalho entre os sistemas do Francês Nativo: um botão de grade abre a lista.

  Em cima, os sistemas que a pessoa tem; a divisória "Conheça também"; embaixo,
  os outros. A lista e quem tem o quê vêm do banco (`plataforma.meus_sistemas`,
  via `meusSistemas()`); é a mesma na plataforma, no Étudie/Lego e nos
  Quadrinhos — cada sistema só desenha. Ícones em `public/sistemas/<codigo>.png`.

  Só aparece com o login único ligado (memoire.uninativ.com). No Mémoire antigo
  de francesnativo.com não há sessão compartilhada, e o botão não aparece.
*/
import { useEffect, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { emailConectado, loginUnicoLigado, meusSistemas, type Sistema } from "@/lib/sessao-unica";

const ESTE_SISTEMA = "memoire";

const COR_DA_INICIAL: Record<string, string> = {
  curso: "bg-[#7B1E2B] text-white",
  memoire: "bg-[#F6F0E9] text-[#1E2A5A]",
  etudie: "bg-[#101B3A] text-white",
  quadrinhos: "bg-[#286B61] text-white",
  lego: "bg-[#E8B23A] text-[#1E2A5A]",
};

function IconeGrade() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
      {[5, 12, 19].flatMap((y) => [5, 12, 19].map((x) => <circle key={`${x}-${y}`} cx={x} cy={y} r="2.1" />))}
    </svg>
  );
}

function Icone({ sistema }: { sistema: Sistema }) {
  const [falhou, setFalhou] = useState(false);
  if (falhou) {
    return (
      <span
        aria-hidden="true"
        className={cn("grid h-10 w-10 flex-none place-items-center rounded-[10px] text-lg font-bold", COR_DA_INICIAL[sistema.codigo] ?? "bg-slate-700 text-white")}
      >
        {sistema.nome.charAt(0)}
      </span>
    );
  }
  return (
    <img
      src={`/sistemas/${sistema.codigo}.png`}
      alt=""
      width={40}
      height={40}
      className="h-10 w-10 flex-none rounded-[10px] object-cover"
      onError={() => setFalhou(true)}
    />
  );
}

function Item({ sistema }: { sistema: Sistema }) {
  const aqui = sistema.codigo === ESTE_SISTEMA;
  return (
    <li>
      <a
        href={sistema.url}
        aria-current={aqui ? "page" : undefined}
        className={cn(
          "flex items-center gap-3 rounded-xl px-3 py-2 text-slate-900 no-underline outline-none hover:bg-slate-100 focus-visible:ring-2 focus-visible:ring-[#013079]",
          aqui && "bg-[#EEF2FA]",
        )}
      >
        <Icone sistema={sistema} />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="text-[15px] font-semibold leading-tight">{sistema.nome}</span>
          <span className="truncate text-[13px] text-slate-500">{sistema.descricao}</span>
        </span>
        {aqui ? (
          <span className="flex-none rounded-full bg-[#013079]/10 px-2 py-0.5 text-[11px] font-semibold text-[#013079]">Você está aqui</span>
        ) : !sistema.tem_acesso ? (
          <span className="flex-none rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">Conhecer</span>
        ) : null}
      </a>
    </li>
  );
}

export function SeletorDeSistemas({ className }: { className?: string }) {
  const [aberto, setAberto] = useState(false);
  const [sistemas, setSistemas] = useState<Sistema[] | null>(null);
  const [email, setEmail] = useState<string | null>(null);

  useEffect(() => {
    if (!aberto || sistemas) return;
    let ativo = true;
    Promise.all([meusSistemas(), emailConectado()]).then(([s, e]) => {
      if (!ativo) return;
      setSistemas(s ?? []);
      setEmail(e);
    });
    return () => {
      ativo = false;
    };
  }, [aberto, sistemas]);

  if (!loginUnicoLigado) return null;

  // Em cima, os que a pessoa tem e o sistema em que ela está agora.
  const emCima = (s: Sistema) => s.tem_acesso || s.codigo === ESTE_SISTEMA;
  const meus = sistemas?.filter(emCima) ?? [];
  const outros = sistemas?.filter((s) => !emCima(s)) ?? [];

  return (
    <Popover open={aberto} onOpenChange={setAberto}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Sistemas do Francês Nativo"
          className={cn("inline-flex h-10 w-10 items-center justify-center rounded-xl text-white/80 hover:bg-white/10 hover:text-white data-[state=open]:bg-white/15", className)}
        >
          <IconeGrade />
        </button>
      </PopoverTrigger>
      {/* Fundo e texto explícitos: o `bg-popover` do tema do Mémoire é amarelo. */}
      <PopoverContent align="end" sideOffset={8} className="w-[min(360px,92vw)] rounded-2xl border-slate-200 bg-white p-0 py-2 text-slate-900 shadow-xl">
        {email && (
          <p className="truncate px-5 pb-2 pt-2 text-xs text-slate-500">
            Conectado como <strong className="font-semibold text-slate-800">{email}</strong>
          </p>
        )}
        {!sistemas ? (
          <p className="px-5 py-3 text-sm text-slate-500">Carregando…</p>
        ) : (
          <>
            {meus.length > 0 && (
              <ul className="m-0 list-none px-2" aria-label="Seus sistemas">
                {meus.map((s) => <Item key={s.codigo} sistema={s} />)}
              </ul>
            )}
            {outros.length > 0 && (
              <>
                <div role="separator" className="mx-5 mb-1 mt-2 flex items-center gap-2.5 text-[11px] uppercase tracking-[.12em] text-slate-500 before:h-px before:flex-1 before:bg-slate-200 after:h-px after:flex-1 after:bg-slate-200">
                  <span>Conheça também</span>
                </div>
                <ul className="m-0 list-none px-2" aria-label="Conheça também">
                  {outros.map((s) => <Item key={s.codigo} sistema={s} />)}
                </ul>
              </>
            )}
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
